import { User } from '../../../types/shared';
import { Match, Show, Switch, useContext } from 'solid-js';
import { A, useNavigate } from '@solidjs/router';
import clsx from 'clsx';
import { AppContext } from '../../../app-context-provider/app-context-provider';
import { UserOrderHistory } from '../../order-history/user-order-history';
import { AccountEducationContent } from '../../account-education-content/account-education-content';
import { MyContent } from '../../my-content/my-content';
import { GraduationCapIcon } from '../../icons-library/graduation-cap';
import { HeartIcon } from '../../icons-library/heart';
import { PdfBuilderIcon } from '../../icons-library/pdf-builder-icon';
import { AccountView } from '../../../tools/account-slugs';
import { Heading } from '../../../ui-components/heading/heading';
import { cviSemantic } from '../../../style/theme';

export type UserProfileMobileProps = {
    user: User | null;
    view: AccountView;
    handleSignOutClick: () => void;
};

export const UserProfileMobile = (props: UserProfileMobileProps) => {
    const {
        localize,
        siteInfo,
        coursesListingPage,
        pdfBuilderPage,
        translations,
        userProfileUrls,
    } = useContext(AppContext);

    const navigate = useNavigate();

    const navItemClasses = (active?: boolean) => clsx(
        'flex items-center gap-cvi-5 w-fit text-left no-underline',
        'body-md font-cvi-regular cursor-pointer bg-transparent border-0 p-0',
        'transition-colors duration-200',
        active
            ? 'text-cvi-fg-link hover:text-cvi-fg-brand'
            : 'text-cvi-fg-default hover:text-cvi-fg-link',
    );

    const SectionHeader = (p: { children: string }) => (
        <Heading tag="h3" variant="heading-xl">
            {p.children}
        </Heading>
    );

    return (
        <div class="flex flex-col px-cvi-6 pt-cvi-9 pb-cvi-10">
            {/* Greeting */}
            <div class="flex items-end justify-between gap-cvi-4 border-b border-cvi-border-muted py-cvi-4 mb-cvi-9">
                <Heading tag="h1" variant="heading-xl" simpleColor={cviSemantic.fgBrand} noBlockSpacing class="min-w-0 break-words">
                    {`${localize('profile-greeting', 'Hi')} ${props.user?.firstName || ''}`}
                </Heading>

                <button
                    class="link-as-button bg-transparent border-0 p-0 shrink-0"
                    onClick={() => props.handleSignOutClick()}
                >
                    {localize('sign-out', 'Sign out')}
                </button>
            </div>

            <Show when={siteInfo.siteType === 'default'}>
                <nav class="flex flex-col gap-cvi-8 mb-cvi-3">
                    <section class="flex flex-col gap-cvi-4">
                        <SectionHeader>{localize('my-content', 'My content')}</SectionHeader>
                        <ul class="flex flex-col gap-cvi-4 list-none p-0 m-0">
                            <Show when={coursesListingPage.url}>
                                <li>
                                    <A
                                        href={userProfileUrls.learning}
                                        class={navItemClasses(props.view === 'learning')}
                                        aria-current={props.view === 'learning' ? 'page' : undefined}
                                    >
                                        <GraduationCapIcon width={2.55} height={2.55} ariaHidden />
                                        <span>{localize('e-learning', 'E-learning')}</span>
                                    </A>
                                </li>
                            </Show>
                            <li>
                                <A
                                    href={userProfileUrls.bookmarks}
                                    class={navItemClasses(props.view === 'bookmarks')}
                                    aria-current={props.view === 'bookmarks' ? 'page' : undefined}
                                >
                                    <HeartIcon width={2.55} height={2.55} ariaHidden />
                                    <span>{localize('bookmarks', 'Bookmarks')}</span>
                                </A>
                            </li>
                        </ul>
                    </section>

                    <Show when={pdfBuilderPage.url}>
                        <section class="flex flex-col gap-cvi-4">
                            <SectionHeader>{localize('profile-tools', 'Tools')}</SectionHeader>
                            <ul class="flex flex-col gap-cvi-4 list-none p-0 m-0">
                                <li>
                                    <button
                                        class={navItemClasses()}
                                        onClick={() => navigate(pdfBuilderPage.url)}
                                    >
                                        <PdfBuilderIcon width={2.55} height={2.55} ariaHidden />
                                        <span>{translations['profile-pdf-builder-heading'] || pdfBuilderPage.title}</span>
                                    </button>
                                </li>
                            </ul>
                        </section>
                    </Show>
                </nav>
            </Show>

            <hr class="border-0 border-t border-cvi-border-muted mt-cvi-9 mb-cvi-10" />

            <div class="min-w-0">
                <Switch>
                    <Match when={props.view === 'bookmarks'}>
                        <MyContent />
                    </Match>
                    <Match when={props.view === 'learning'}>
                        <AccountEducationContent />
                    </Match>
                    <Match when={props.view === 'orders'}>
                        <UserOrderHistory />
                    </Match>
                </Switch>
            </div>
        </div>
    );
};
